import { notification } from "antd";
import { useCallback, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import {
  useConstatAskValidationMutation,
  useConstatReopenMutation,
  useCreateEventCommentMutation,
  useDeleteConstatMutation,
  useFetchConstatCommentsQuery,
} from "../../apiSlice";
import config from "../../../../config";
import { CommentPostValues } from "../../../../types/comments";
import { useAppSelector } from "../../../redux-store/hooks";
import { getErrorMessage } from "../../../../utils/errors/errors";

type Options = {
  fetchConstats?: () => void;
  fetchConstat?: () => void;
  navigate?: NavigateFunction;
  constatId?: number;
};

export const useCrudConstat = ({
  fetchConstats,
  fetchConstat,
  navigate,
  constatId,
}: Options = {}) => {
  /**
   * Delete/Archive
   */
  const [deleteConstat, { isLoading: isDeleting }] = useDeleteConstatMutation();

  const onDelete = useCallback(
    async (id: number) => {
      try {
        await deleteConstat(id).unwrap();
        notification.success({ message: "Constat supprimÃ© avec succÃ¨s." });
        if (fetchConstats) {
          fetchConstats();
        }
      } catch (error) {
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [deleteConstat, fetchConstats],
  );

  const [archiveConstatRequest, { isLoading: isArchiving }] =
    useDeleteConstatMutation();
  const archiveConstat = useCallback(
    async (id: number) => {
      try {
        await archiveConstatRequest(Number(id)).unwrap();
        notification.success({
          message: "Le constat a Ã©tÃ© archivÃ© avec succÃ¨s",
        });
        if (navigate) {
          navigate("/pdca/constat");
        } else {
          console.warn("The 'archiveConstat' fn needs 'navigate' to be defined");
        }
      } catch (error) {
        console.error("archiveConstat error", error);
      }
    },
    [archiveConstatRequest, navigate],
  );

  /**
   * Ask validation
   */
  const [askValidation, { isLoading: isAskingValidation }] =
    useConstatAskValidationMutation();
  const onAskValidation = useCallback(
    async (id: number) => {
      try {
        await askValidation(id).unwrap();
        notification.success({
          message: "Demande de validation effectuÃ©e.",
        });
        if (fetchConstat) {
          fetchConstat();
        } else {
          console.warn(
            "The 'askValidation' fn needs 'fetchConstat' to be defined",
          );
        }
      } catch (error) {
        console.error("askValidation error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [askValidation, fetchConstat],
  );

  /**
   * Reopen
   */
  const [reopen, { isLoading: isReopening }] = useConstatReopenMutation();
  const onReopen = useCallback(
    async (id: number) => {
      try {
        await reopen(id).unwrap();
        notification.success({
          message: "Constat rÃ©ouvert",
          description: `Le constat a Ã©tÃ© rÃ©ouvert avec succÃ¨s.`,
        });
        if (fetchConstat) {
          fetchConstat();
        } else {
          console.warn("The 'reopen' fn needs 'fetchConstat' to be defined");
        }
      } catch (error) {
        console.error("reopen error", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [reopen, fetchConstat],
  );

  /**
   * Comments
   */
  const { data: comments, refetch: refetchComments } =
    useFetchConstatCommentsQuery(constatId as number, { skip: !constatId });
  const [createCommentRequest, { isLoading: isCreatingComment }] =
    useCreateEventCommentMutation();
  const createComment = useCallback(
    async (values: Pick<CommentPostValues, "description">) => {
      if (!constatId) return;
      try {
        await createCommentRequest({
          description: values.description,
          commentType: "CONSTAT",
          constat: constatId,
        }).unwrap();
        notification.success({ message: "Commentaire ajoutÃ©." });
        refetchComments();
      } catch (error) {
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createCommentRequest, constatId, refetchComments],
  );

  /**
   * Export PDF
   */
  const accessToken = useAppSelector((state) => state.auth.user?.access_token);
  const [isExporting, setIsExporting] = useState(false);
  const exportPdf = useCallback(
    async (id: number) => {
      setIsExporting(true);
      try {
        const response = await fetch(`${config.apiUrl}/evt/constats/${id}/export`, {
          headers: { Authorization: `Bearer ${accessToken ?? ""}` },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || `Erreur ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `constat_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);

        notification.success({ message: "Export PDF tÃ©lÃ©chargÃ© avec succÃ¨s." });
      } catch (error) {
        notification.error({
          message: "Ãchec de l'export PDF",
          description: getErrorMessage(error),
        });
      } finally {
        setIsExporting(false);
      }
    },
    [accessToken],
  );

  return {
    // Delete/archive
    onDelete,
    isDeleting,
    archiveConstat,
    isArchiving,
    // Ask validation
    onAskValidation,
    isAskingValidation,
    // Reopen
    onReopen,
    isReopening,
    // Export PDF
    exportPdf,
    isExporting,
    // Comments
    comments,
    createComment,
    isCreatingComment,
  };
};
